import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * WhatsApp-style poll creation modal.
 * Fields: title, dynamic options (add/remove), "Birden fazla seçim yapılabilir" checkbox.
 */
const PollCreateModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation('chat');
  const [title, setTitle] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multipleChoice, setMultipleChoice] = useState(false);
  const [secretVoting, setSecretVoting] = useState(false);

  const addOption = () => {
    if (options.length >= 5) return;
    setOptions([...options, '']);
  };

  const removeOption = (index) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index, value) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedOptions = options.map((o) => o.trim()).filter(Boolean);
    if (!trimmedTitle) return;
    if (trimmedOptions.length < 2) return;
    onSubmit({
      poll_title: trimmedTitle,
      poll_options: trimmedOptions,
      poll_multiple_choice: multipleChoice,
      poll_secret: secretVoting,
    });
    setTitle('');
    setOptions(['', '']);
    setMultipleChoice(false);
    setSecretVoting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-md bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-xl border border-white/10 dark:border-white/10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#3d8ef0]" />
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">{t('pollCreateModal.title')}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#24354f] hover:text-[var(--color-text-secondary)] dark:hover:text-gray-200"
              aria-label={t('common:close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-1">
                {t('pollCreateModal.titleLabel')}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('pollCreateModal.titlePlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] focus:ring-2 focus:ring-[#3d8ef0]/50 focus:border-transparent"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">
                  {t('pollCreateModal.optionsLabel')}
                </label>
                {options.length < 5 && (
                  <button
                    type="button"
                    onClick={addOption}
                    className="flex items-center gap-1 text-sm text-[#3d8ef0] dark:text-[#7ab8f8] hover:underline"
                  >
                    <Plus className="w-4 h-4" />
                    {t('pollCreateModal.addOption')}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {options.map((opt, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={t('pollCreateModal.optionPlaceholder', { number: index + 1 })}
                      className="flex-1 px-3 py-2 rounded-lg border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] focus:ring-2 focus:ring-[#3d8ef0]/50 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      disabled={options.length <= 2}
                      className="p-2 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#24354f] hover:text-red-500 disabled:opacity-40 disabled:pointer-events-none"
                      aria-label={t('pollCreateModal.removeOption')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={multipleChoice}
                onChange={(e) => setMultipleChoice(e.target.checked)}
                className="w-4 h-4 rounded border-white/10 text-[#3d8ef0] focus:ring-[#3d8ef0]/50"
              />
              <span className="text-sm text-[var(--color-text-secondary)] dark:text-gray-300">
                {t('pollCreateModal.multipleChoiceLabel')}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={secretVoting}
                onChange={(e) => setSecretVoting(e.target.checked)}
                className="w-4 h-4 rounded border-white/10 text-[#3d8ef0] focus:ring-[#3d8ef0]/50"
              />
              <span className="text-sm text-[var(--color-text-secondary)] dark:text-gray-300">
                {t('pollCreateModal.secretVotingLabel')}
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 dark:border-white/10 text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-muted)] dark:hover:bg-[#24354f]"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={!title.trim() || options.filter((o) => o.trim()).length < 2}
                className="flex-1 px-4 py-2 rounded-lg bg-[#2563eb] text-white hover:bg-[#3d8ef0] disabled:opacity-50 disabled:pointer-events-none"
              >
                {t('pollCreateModal.send')}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PollCreateModal;
